import { authHeaders, handleUnauthorized } from './http';

const BASE = '/api';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(init?.headers || {}) },
    ...init,
  });
  handleUnauthorized(res);
  if (!res.ok) throw new Error((await res.text()) || res.statusText);
  return res.json();
}

export const authApi = {
  status: () => req<{ auth_required: boolean }>('/auth/status'),
  login: (username: string, password: string) =>
    req<{ token: string; user: { username: string; full_name?: string | null; role: string } }>('/auth/login', {
      method: 'POST', body: JSON.stringify({ username, password }),
    }),
  me: () => req<{ username: string; role: string }>('/auth/me'),
  changePassword: (current_password: string, new_password: string) =>
    req('/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password, new_password }) }),
  users: () => req<any[]>('/auth/users'),
  createUser: (body: { username: string; password: string; full_name?: string; role: string }) =>
    req('/auth/users', { method: 'POST', body: JSON.stringify(body) }),
  setActive: (username: string, active: boolean) =>
    req(`/auth/users/${encodeURIComponent(username)}/active?active=${active}`, { method: 'PATCH' }),
  resetPassword: (username: string, new_password: string) =>
    req(`/auth/users/${encodeURIComponent(username)}/reset-password`, { method: 'POST', body: JSON.stringify({ new_password }) }),
};

export const shocksApi = {
  overview: () => req<any>('/shocks'),
  banner: () => req<any>('/shocks/banner'),
  scan: (only?: string) => req<any>(`/shocks/scan${only ? `?only=${encodeURIComponent(only)}` : ''}`, { method: 'POST' }),
  override: (id: string, body: { manual_scale?: number | null; manual_note?: string | null; active?: boolean }) =>
    req<any>(`/shocks/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  history: () => req<any[]>('/shocks/history'),
};

export const growthApi = {
  overview: () => req<any>('/growth/overview'),
  elasticities: () => req<any>('/growth/elasticities'),
  registry: () => req<any>('/growth/indicator-registry'),
  equations: () => req<any>('/growth/equations'),
  assumptions: () => req<any>('/growth/assumptions'),
  workbookPresets: () => req<any>('/growth/workbook/presets'),
  workbookRun: (preset: string, unitCorrected: boolean) =>
    req<any>(`/growth/workbook/run?preset=${preset}&unit_corrected=${unitCorrected}`),
  workbookRunCustom: (body: { preset?: string; unit_corrected: boolean; inputs: Record<string, number> }) =>
    req<any>('/growth/workbook/run', { method: 'POST', body: JSON.stringify(body) }),
  trace: (targetGdp: number, targetYear: number, strategy: string, currentYear = 2026) =>
    req<any>(`/growth/trace?target_gdp=${targetGdp}&target_year=${targetYear}&strategy=${strategy}&current_year=${currentYear}`),
  editElasticity: (id: string, body: unknown) => req<any>(`/growth/elasticities/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  editLinkage: (id: string, body: unknown) => req<any>(`/growth/indicator-linkages/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  lever: (variableId: string) => req<any>(`/growth/levers/${encodeURIComponent(variableId)}`),
  editLever: (variableId: string, body: unknown) => req<any>(`/growth/levers/${encodeURIComponent(variableId)}`, { method: 'PUT', body: JSON.stringify(body) }),
  scenarioTargets: () => req<any>('/growth/scenario-targets'),
  setScenarioTargets: (targets: Record<string, number | null>, calibration_mode?: string) => req<any>('/growth/scenario-targets', { method: 'PUT', body: JSON.stringify({ targets, calibration_mode }) }),
  constraintMode: () => req<{ mode: string; modes: string[]; descriptions: Record<string, string> }>('/growth/constraints/mode'),
  setConstraintMode: (mode: string) => req<{ mode: string }>('/growth/constraints/mode', { method: 'POST', body: JSON.stringify({ mode }) }),
  executive: (scenario: string) => req<any>(`/growth/executive?scenario=${scenario}`),
  scenarioContributors: (scenarioId: string, startYear = 2026) => req<any>(`/growth/scenario/${scenarioId}/contributors?start_year=${startYear}`),
  macroEnvelope: (targetGdp: number) => req<any>(`/growth/macro-envelope?target_gdp=${targetGdp}`),
  decisions: () => req<any>('/growth/decisions'),
  createDecision: (body: unknown) => req<any>('/growth/decisions', { method: 'POST', body: JSON.stringify(body) }),
  patchDecision: (id: string, body: unknown) => req<any>(`/growth/decisions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteDecision: (id: string) => req<any>(`/growth/decisions/${id}`, { method: 'DELETE' }),
  programmes: () => req<any>('/growth/programmes'),
  createProgramme: (body: unknown) => req<any>('/growth/programmes', { method: 'POST', body: JSON.stringify(body) }),
  patchProgramme: (id: string, body: unknown) => req<any>(`/growth/programmes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  economistBrief: (targetGdp?: number, targetYear = 2029) =>
    req<any>(`/growth/economist/brief?target_year=${targetYear}${targetGdp ? `&target_gdp=${targetGdp}` : ''}`),
};
